import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { articleCrumbs, breadcrumbJsonLd, getTopic } from '../articles';
import { Breadcrumb, FitCheckBox, MoreTopics, QuickLink } from '../components/articles/ArticleParts';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import '../components/svs-guide/svs-guide.css';

// English counterpart of UmsatzsteuerGrundlagenGuide.tsx — see that file's
// header comment for the V1/T1-T4 context. Structure mirrors
// SvsSocialInsuranceGuide.tsx / SvsSozialversicherungGuide.tsx as closely as
// the content allows.
// Published article (src/articles.ts) — prerenderRoutes.ts emits the
// matching robots tag for the static HTML.
const INDEXABLE = true;

const TOPIC = getTopic('vat');
const ARTICLE = TOPIC.en;
const CRUMBS = articleCrumbs(TOPIC, 'en');
const PATH = ARTICLE.path;
const CANONICAL = `https://pstax.at${PATH}`;
export const TITLE_VAT_EN = ARTICLE.title;
export const DESCRIPTION_VAT_EN = ARTICLE.description;
const H1 = ARTICLE.h1;

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Certified tax advisor (Steuerberater)',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_VAT_EN = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: ARTICLE.datePublished,
  dateModified: ARTICLE.dateModified,
  inLanguage: 'en',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'VAT for the self-employed in Austria',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'VAT is never your money', id: 'never-your-money' },
  { label: 'How much is VAT in Austria?', id: 'rate' },
  { label: 'Input tax deduction: your offset for buying things', id: 'input-tax' },
  { label: 'What invoice you need for the input tax deduction', id: 'invoice' },
  { label: 'How often you file a VAT return (UVA)', id: 'uva' },
  { label: 'Do you even have to charge VAT?', id: 'small-business' },
  { label: 'Clients in other EU countries', id: 'eu-clients' },
  { label: 'When opting into VAT voluntarily is worth it', id: 'opting-in' },
  { label: 'Mistakes we see most often', id: 'mistakes' },
  { label: 'Frequently asked questions', id: 'faq' },
  { label: 'Official sources', id: 'sources' },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_VAT_EN: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'Do I have to charge VAT as a freelancer in Austria?',
    "Only if you're not using the small-business exemption (up to €55,000 annual revenue) or have voluntarily opted out of it.",
    // F-V2, F-V6
  ),
  plainFaq(
    'What happens if I forgot to charge VAT?',
    'You still owe it to the tax office – adding it retroactively with the client is often difficult, so the money comes out of your own pocket.',
  ),
  plainFaq(
    'Can I reclaim VAT on a laptop I bought for my business?',
    "Yes, as input tax, if you're not a small business and have a proper invoice.",
    // F-V7
  ),
  plainFaq(
    'Is VAT the same as sales tax?',
    'In everyday language, yes; the legal term in Austria is Umsatzsteuer (USt).',
  ),
  plainFaq(
    'When is the VAT return (UVA) due?',
    'On the 15th of the second following month – for quarterly filers, e.g. 15 February, May, August, November.',
    // F-V11
  ),
  plainFaq(
    'Do I charge Austrian VAT to clients abroad?',
    'For B2B services to businesses in the EU, usually not (reverse charge) – but you’ll generally need a VAT ID (UID).',
    // F-V8, F-V9
  ),
  plainFaq(
    'What happens if I exceed the small-business threshold?',
    'Up to €60,500 the exemption still runs until year-end; above that it ends immediately – from the transaction that crosses the line.',
    // F-V3
  ),
  plainFaq(
    'Do I need a VAT ID (UID) as a small business?',
    "Only if you do business with companies in other EU countries; it doesn't change your tax-exempt status.",
    // F-V8
  ),
  plainFaq(
    'Can I voluntarily opt out of the small-business exemption?',
    'Yes, with form U12 – but that locks you in for at least 5 calendar years.',
    // F-V6
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, opens in a new tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Soon
  </span>
);

export const VatBasicsGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_VAT_EN,
    description: DESCRIPTION_VAT_EN,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'en',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/vat-basics-austria/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/umsatzsteuer-grundlagen/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/vat-basics-austria/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_VAT_EN,
      breadcrumbJsonLd(CRUMBS, PATH),
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_VAT_EN.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <Breadcrumb crumbs={CRUMBS} lang="en" />

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Articles · VAT
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-4 leading-tight">
              {H1}
            </h1>
            <QuickLink topic={TOPIC} lang="en" />
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              You'll hear wildly different things from other freelancers: one never charges VAT, another claims
              back thousands of euros a year. Both can be right – it just depends which situation you're in.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                As of: <time dateTime="2026-11-02">November 2026</time> · Reviewed by{' '}
                <a href="#author" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="DE" label="Diese Seite auf Deutsch →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">At a glance</h2>
              <ul className="space-y-2">
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>Standard VAT rate: 20%{/* F-V1 */}</span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Small-business exemption (<span lang="de">Kleinunternehmerregelung</span>) threshold: €55,000
                    gross/year, unchanged for 2025 and 2026{/* F-V2 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Tolerance up to €60,500: the exemption continues until year-end, then ends immediately{/* F-V3 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Input tax deduction (<span lang="de">Vorsteuerabzug</span>) needs a proper invoice; a simplified
                    invoice works up to €400 gross{/* F-V7 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    VAT return (<span lang="de">Umsatzsteuervoranmeldung</span>, UVA): monthly above €100,000
                    prior-year revenue, otherwise quarterly, below €55,000 usually not needed at all{/* F-V11 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 py-1.5 text-sm text-pstax-navy">
                  <span>Opting into VAT voluntarily locks you in for 5 years{/* F-V6 */}</span>
                </li>
              </ul>
            </div>

            <p className="mb-10 leading-relaxed text-pstax-gray">
              Is the small-business exemption right for you? → Small-business VAT exemption guide
              <SoonBadge />
            </p>

            {/* Table of contents */}
            <nav className="toc-top mb-12 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8" aria-label="On this page">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">On this page</p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* VAT is never your money */}
            <h2 id="never-your-money" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              VAT is never your money
            </h2>
            <p className="mb-4 leading-relaxed">
              You add it to your invoice, collect it from your client, and pass it on to the tax office.
              Economically, you're not the one paying it – your client is.
            </p>
            <p className="mb-4 leading-relaxed">
              Example: you invoice €1,000 net + €200 VAT, your business client pays €1,200 gross. You pass the
              €200 on to the tax office, your client claims it back as input tax. Net effect for a business client:
              €0. The only people who actually pay VAT are private end consumers, who get nothing back.
            </p>
            <Callout tone="orange" label="Sounds simple, but">
              A faulty invoice can cost your client their input tax deduction – and leave you owing VAT you never
              actually charged.
            </Callout>

            {/* How much is VAT */}
            <h2 id="rate" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              How much is VAT in Austria?
            </h2>
            <p className="mb-8 leading-relaxed">
              The standard rate is 20%, with reduced rates of 10% or 13% for certain services{/* F-V1 */}. That's
              unchanged for 2025 and 2026; from 1 July 2026 a new 4.9% rate applies to selected basic foods – not
              relevant for most freelance services.
            </p>

            {/* Input tax deduction */}
            <h2 id="input-tax" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Input tax deduction (<span lang="de">Vorsteuerabzug</span>): your offset for buying things
            </h2>
            <p className="mb-4 leading-relaxed">
              The VAT you pay yourself when you buy something comes back to you as input tax – you effectively
              only pay the difference between the VAT you charge and the VAT you've paid{/* F-V7 */}.
            </p>
            <p className="mb-4 leading-relaxed">
              Example: you buy a laptop for €1,200 gross (incl. €200 VAT) and get the €200 back.
            </p>
            <Callout tone="orange" label="Sounds simple, but">
              No deduction without a proper invoice, and never for purely private purchases.
            </Callout>

            {/* Invoice requirements */}
            <h2 id="invoice" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              What invoice you need for the input tax deduction
            </h2>
            <p className="mb-4 leading-relaxed">
              For the full input tax deduction you need an invoice with all the legally required details; up to
              €400 gross, a simplified invoice (<span lang="de">Kleinbetragsrechnung</span>) is
              enough{/* F-V7 */}.
            </p>
            <p className="mb-8 leading-relaxed">
              What this means for you: check incoming invoices are complete before you file them – one missing
              detail can cost you the entire deduction, not just part of it.
            </p>

            {/* UVA */}
            <h2 id="uva" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              How often you file a VAT return (UVA)
            </h2>
            <p className="mb-8 leading-relaxed">
              It depends on your prior-year revenue: above €100,000, monthly; up to €100,000, quarterly; below
              €55,000, usually not at all – though the amount owed is still due{/* F-V11 */}. The UVA is due on
              the 15th of the second following month – for quarterly filers that's 15 February, May, August and
              November.
            </p>

            {/* Small business */}
            <h2 id="small-business" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Do you even have to charge VAT?
            </h2>
            <p className="mb-8 leading-relaxed">
              Not necessarily: as a small business (<span lang="de">Kleinunternehmer</span>) with up to €55,000
              annual revenue, you can stay exempt from VAT{/* F-V2 */} – but then you also don't get any input
              tax back{/* F-V5 */}. Which option makes sense for you mostly depends on your clients and expenses
              (more in the small-business exemption guide
              <SoonBadge />).
            </p>

            {/* EU clients */}
            <h2 id="eu-clients" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Clients in other EU countries
            </h2>
            <p className="mb-8 leading-relaxed">
              If you provide services to a business in another EU country, you usually don't charge Austrian VAT. Your
              client pays the VAT in their own country (reverse charge). To do this, you need a VAT ID (UID), even
              under the small-business exemption. Your invoice must show your VAT ID, your client's VAT ID and a note
              that the tax liability passes to the recipient. You also report these sales to the tax office regularly
              in a recapitulative statement (<span lang="de">Zusammenfassende Meldung</span>, ZM). Different rules
              apply to private clients and to selling goods.
            </p>

            {/* Opting in */}
            <h2 id="opting-in" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              When opting into VAT voluntarily is worth it
            </h2>
            <p className="mb-8 leading-relaxed">
              If most of your clients are businesses that reclaim VAT anyway, or you're planning bigger
              investments, voluntarily opting out of the small-business exemption can be worth it – you then get
              the VAT back on your purchases. Sounds simple, but: opting in (
              <span lang="de">Regelbesteuerungsantrag</span>) locks you in for at least 5 calendar
              years{/* F-V6 */}. Not a decision to make on a whim.
            </p>

            {/* Mistakes */}
            <h2 id="mistakes" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Mistakes we see most often
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>Issuing invoices without VAT after no longer qualifying as a small business</li>
              <li>Deducting input tax without a proper invoice – usually only caught in an audit</li>
              <li>Crossing the small-business threshold and reacting months too late</li>
              <li>
                Missing reverse charge on invoices from Google, Meta or other foreign providers{/* F-V9 */}
              </li>
              <li>Opting out of the small-business exemption without pricing in the 5-year lock-in</li>
            </ul>

            <p className="mb-10 leading-relaxed">
              <strong>What does this mean for me?</strong> Whether the small-business exemption or VAT liability
              fits you depends on your clients and expenses – and on a 5-year lock-in if you change your mind. We
              work through it together in a free first call.{' '}
              {/* TODO: PublicOnly/InternalOnly once T1 ships.
                  InternalOnly: "Questions about this? Message us in the portal or at clients@pstax.at" */}
              <Link to="/en/#fit-check" className="text-pstax-link hover:underline font-semibold">
                Book a free first call
              </Link>
              .
            </p>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_VAT_EN.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            <p className="mb-10 leading-relaxed text-sm text-pstax-gray">
              <strong className="text-pstax-navy">Keep reading</strong> 2-minute quick guide on this topic
              <SoonBadge /> · Small-business VAT exemption
              <SoonBadge /> · VAT ID number
              <SoonBadge />
            </p>

            {/* Sources */}
            <h2 id="sources" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Official sources
            </h2>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/steuersaetze-und-steuerbefreiungen-der-umsatzsteuer.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  VAT overview – rates
                </ExternalLink>
                {/* F-V1 */}
              </li>
              <li>
                usp.gv.at / wko.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/weitere-steuertatbestaende-und-befreiungen/kleinunternehmen.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Small-business exemption (<span lang="de">Kleinunternehmerregelung</span>)
                </ExternalLink>
                {/* F-V2, F-V3, F-V6 */}
              </li>
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/vorsteuerabzug-und-rechnung/fuer-den-vorsteuerabzug-notwendige-rechnungsmerkmale.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Invoice requirements for input tax deduction
                </ExternalLink>
                {/* F-V7 */}
              </li>
              <li>
                usp.gv.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/entstehen-der-steuerschuld-und-pflichten/umsatzsteuervoranmeldung.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  VAT return (<span lang="de">Umsatzsteuervoranmeldung</span>)
                </ExternalLink>
                {/* F-V11 */}
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">Not sure where you stand?</h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Whether the small-business exemption or VAT liability fits you depends on your clients and
                expenses. In a free 30-minute first call, we work it out for you.{' '}
                {/* TODO: PublicOnly/InternalOnly once T1 ships.
                    InternalOnly: "Already a client? We answer questions like this in the portal or at
                    clients@pstax.at." */}
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/en/#fit-check" className="btn-accent">
                  Book your free call
                </Link>
              </div>
              <p className="text-xs text-white/50">
                PSTax advises 600+ clients in Austria, fixed prices from €800/year, in German and
                English.
              </p>
            </div>

            {/* Author box */}
            <div id="author" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, Austrian certified tax advisor and founder of PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Pascal Schraml</p>
              <p className="text-sm text-pstax-gray mb-3">
                Austrian certified tax advisor (<span lang="de">Steuerberater</span>), founder of PSTax
              </p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Advising self-employed people and freelancers in Austria since 2023, bilingual service in German
                and English, 600+ clients.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                General information, not individual tax advice. All figures apply to 2026 and change annually.
                PSTax (Pascal Schraml Steuerberatung FlexCo) is a member of the Austrian Chamber of Tax Advisors
                and Auditors (KSW).
              </p>
            </div>

            <MoreTopics currentKey={TOPIC.key} lang="en" />
            <FitCheckBox lang="en" />
          </article>
      </div>
    </div>
  );
};
